import { ValidationQuestion } from "../interfaces/document-category.interface";
import {
  extractFormValueFromAnswer,
  hasMapCaptureEvidence,
  hasResolvableQuestionDisplayText,
  hasStoredQuestionAnswer,
} from "./question-stepper-answer-display.util";
import { hasTableDisplayEvidence, isOverviewFieldComplement } from "./question-stepper-overview-display.util";

/** Current question-stepper-v2 payload version sent/received from backend. */
export const QUESTION_STEPPER_V2_VERSION = "2.0";

/** Inclusive lower bound for question-stepper-v2 (major 2.x). */
export const QUESTION_STEPPER_V2_MIN_VERSION = "2.0";

/** Exclusive upper bound for question-stepper-v2 (next major). */
export const QUESTION_STEPPER_V2_MAX_VERSION = "3.0";

export interface QuestionRecordMeta {
  version?: string;
}

/** Parses a dotted numeric version into comparable number parts (e.g. "2.1.0" → [2, 1, 0]). */
function parseVersionParts(version: string): number[] | null {
  const trimmed = version.trim();
  if (!/^\d+(\.\d+)*$/.test(trimmed)) {
    return null;
  }
  return trimmed.split(".").map((part) => Number(part));
}

/** Compares two dotted numeric versions. Returns negative if a < b, 0 if equal, positive if a > b. */
function compareVersions(a: string, b: string): number | null {
  const partsA = parseVersionParts(a);
  const partsB = parseVersionParts(b);
  if (!partsA || !partsB) {
    return null;
  }
  const length = Math.max(partsA.length, partsB.length);
  for (let i = 0; i < length; i++) {
    const valueA = partsA[i] ?? 0;
    const valueB = partsB[i] ?? 0;
    if (valueA !== valueB) {
      return valueA - valueB;
    }
  }
  return 0;
}

/** Returns true when the version is in the question-stepper-v2 range (>= 2.0 and < 3.0). */
export function isQuestionStepperV2Version(version: string | null | undefined): boolean {
  if (!version?.trim()) {
    return false;
  }
  const comparedToMin = compareVersions(version, QUESTION_STEPPER_V2_MIN_VERSION);
  const comparedToMax = compareVersions(version, QUESTION_STEPPER_V2_MAX_VERSION);
  if (comparedToMin == null || comparedToMax == null) {
    return false;
  }
  return comparedToMin >= 0 && comparedToMax < 0;
}

/** Resolves question stepper version from persisted record metadata or question payload. */
export function resolveQuestionStepperVersion(
  questions: Partial<ValidationQuestion>[] | null | undefined,
  meta?: QuestionRecordMeta | null
): string | undefined {
  const fromMeta = meta?.version?.trim();
  if (fromMeta) {
    return fromMeta;
  }
  const fromQuestion = questions?.find((question) => question.version?.trim())?.version?.trim();
  return fromQuestion || undefined;
}

/** Returns true when questions should render with question-stepper-v2. */
export function usesQuestionStepperV2(
  questions: Partial<ValidationQuestion>[] | null | undefined,
  meta?: QuestionRecordMeta | null
): boolean {
  const version = resolveQuestionStepperVersion(questions, meta);
  return isQuestionStepperV2Version(version);
}

function resolveOptionComparableValue(optionValue: unknown): unknown {
  if (optionValue != null && typeof optionValue === "object" && "value" in (optionValue as Record<string, unknown>)) {
    return (optionValue as { value: unknown }).value;
  }
  return optionValue;
}

function isOptionSelected(question: Partial<ValidationQuestion>, optionValue: unknown): boolean {
  const selected = extractFormValueFromAnswer(question.answer, { question });
  if (selected == null) {
    return false;
  }
  const comparable = resolveOptionComparableValue(optionValue);
  return selected === comparable || selected === optionValue;
}

/** Flattens v2 question trees (follow_up_questions) into a list for read-only display. */
export function flattenQuestionStepperAnswers(
  questions: Partial<ValidationQuestion>[] | null | undefined
): Partial<ValidationQuestion>[] {
  if (!Array.isArray(questions) || questions.length === 0) {
    return [];
  }

  const flattened: Partial<ValidationQuestion>[] = [];

  const visit = (question: Partial<ValidationQuestion>): void => {
    if (!question) {
      return;
    }

    // Field-style follow-ups (dropdown/input/checkbox/textarea without a script question)
    // are complements of the parent answer, not standalone overview questions.
    if (isOverviewFieldComplement(question)) {
      return;
    }

    const hasQuestionText = hasResolvableQuestionDisplayText(question);
    const hasAnswer = hasStoredQuestionAnswer(question);
    const hasMapCaptures = hasMapCaptureEvidence(question);
    const hasTableEvidence = hasTableDisplayEvidence(question);

    if (hasQuestionText && (hasAnswer || hasMapCaptures || hasTableEvidence)) {
      flattened.push(question);
    }

    const options = question.options as
      | Array<{ value?: unknown; follow_up_questions?: Partial<ValidationQuestion>[] }>
      | undefined;

    if (!options?.length) {
      return;
    }

    for (const option of options) {
      const followUps = option.follow_up_questions;
      if (!followUps?.length) {
        continue;
      }

      if (hasAnswer && !isOptionSelected(question, option.value)) {
        continue;
      }

      for (const followUp of followUps) {
        visit(followUp);
      }
    }
  };

  for (const question of questions) {
    visit(question);
  }

  return flattened;
}
